/**
 * Fehler-Mapping der I18n_Infrastruktur.
 *
 * Bildet einen Fehler_Code einer Backend-Fehlerantwort auf einen lokalisierten
 * Fehler_Übersetzungs-Schlüssel ab. Die Zuordnung erfolgt AUSSCHLIESSLICH anhand
 * des Fehler_Codes (`body.error.code`), nicht anhand des Backend_Fehlertexts
 * (`message`) (Req 12.3). Fehlt der Code oder existiert kein zugeordneter
 * Schlüssel im Katalog, wird der generische Fallback-Schlüssel geliefert
 * (Req 12.4, 12.5).
 */

import { ApiError } from "../lib/apiClient";
import { catalog, DEFAULT_LANGUAGE } from "./catalog";
import type { TranslateParams } from "./translate";

/** Generischer Fehler_Fallbacktext-Schlüssel (Req 12.4, 12.5). */
const FALLBACK_KEY = "errors.fallback";

/**
 * Extrahiert den Fehler_Code aus einem `ApiError`-Objekt.
 *
 * Erwartet das Backend-Envelope `{ error: { code, message } }` in `err.body`.
 * Liefert den Code nur, wenn `err` ein `ApiError` ist und `body.error.code`
 * eine nicht-leere Zeichenkette ist; sonst `null`.
 */
function extractCode(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body;
  if (typeof body !== "object" || body === null || !("error" in body)) {
    return null;
  }
  const errorField = (body as { error: unknown }).error;
  if (
    typeof errorField !== "object" ||
    errorField === null ||
    !("code" in errorField)
  ) {
    return null;
  }
  const code = (errorField as { code: unknown }).code;
  return typeof code === "string" && code.length > 0 ? code : null;
}

/**
 * Ermittelt den Fehler_Übersetzungs-Schlüssel für einen Fehler.
 *
 * - Nutzt ausschließlich `body.error.code` eines `ApiError` (Req 12.3).
 * - Bildet auf `errors.<CODE>` ab, sofern dieser Key im Katalog existiert.
 * - Sonst (fehlender Code, Nicht-`ApiError` oder unbekannter Code) →
 *   `errors.fallback` (Req 12.4, 12.5).
 */
export function errorKey(err: unknown): string {
  const code = extractCode(err);
  if (code === null) return FALLBACK_KEY;

  const key = `errors.${code}`;
  // Der Katalog hält jeden Key in beiden Sprachen; die Prüfung gegen die
  // Standardsprache genügt, um die Existenz des Mappings festzustellen.
  return key in catalog[DEFAULT_LANGUAGE] ? key : FALLBACK_KEY;
}

/**
 * Convenience: liefert direkt den lokalisierten Fehlertext in der Aktive_Sprache
 * über die übergebene Übersetzungs-Funktion `t` (Req 12.1).
 */
export function errorMessage(
  t: (key: string, p?: TranslateParams) => string,
  err: unknown,
): string {
  return t(errorKey(err));
}
